import React from 'react';
import { motion } from 'framer-motion';
import { releases } from '../data/mock';
import { Play, ExternalLink, Headphones } from 'lucide-react';

const ReleasesSection = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 12,
      },
    },
  };

  const handleListenClick = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <section id="releases" className="py-24 lg:py-32 bg-black">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16"
        >
          <div>
            <span className="service-tag" style={{ backgroundColor: '#ff965a', color: '#151515' }}>
              Latest Music
            </span>
            <h2 className="font-display text-4xl md:text-6xl lg:text-7xl font-extrabold uppercase text-white mt-4">
              Releases
            </h2>
          </div>
          <a 
            href="https://audiomack.com/morena-thedeejay" 
            target="_blank" 
            rel="noopener noreferrer"
            className="cta-button self-start md:self-auto"
          >
            View All on Audiomack
            <ExternalLink className="w-4 h-4 ml-2" />
          </a>
        </motion.div>

        {/* Releases Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {releases.map((release) => (
            <motion.div
              key={release.id}
              variants={itemVariants}
              className="group relative cursor-pointer"
              onClick={() => handleListenClick(release.audiomackUrl)}
            >
              {/* Album Art */}
              <div className="relative aspect-square rounded-lg overflow-hidden mb-6">
                <img
                  src={release.image}
                  alt={release.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <button 
                    className="w-16 h-16 rounded-full flex items-center justify-center transition-transform group-hover:scale-110"
                    style={{ backgroundColor: release.color }}
                  >
                    <Play className="w-6 h-6 text-black fill-black ml-1" />
                  </button>
                </div>
                {/* Type Badge */}
                <span 
                  className="absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-mono uppercase tracking-widest"
                  style={{ backgroundColor: release.color, color: '#151515' }}
                >
                  {release.type}
                </span>
                {/* Audiomack Badge */}
                <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-black/70 text-white text-xs font-mono uppercase tracking-widest flex items-center gap-1">
                  <Headphones className="w-3 h-3" />
                  Audiomack
                </div>
              </div>

              {/* Info */}
              <div>
                <h3 className="font-display text-2xl font-bold text-white uppercase mb-2 group-hover:text-light-pink transition-colors">
                  {release.title}
                </h3>
                <p className="text-white/60 text-base mb-1">{release.artist}</p>
                <div className="flex items-center justify-between">
                  <p className="text-white/40 text-sm font-mono">{release.year}</p>
                  <span className="text-light-pink text-sm font-mono uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                    Listen Now <ExternalLink className="w-3 h-3" />
                  </span>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Streaming Platforms */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-20 text-center"
        >
          <p className="text-white/60 font-mono text-sm uppercase tracking-widest mb-6">Also Available On</p>
          <div className="flex flex-wrap items-center justify-center gap-8">
            {['Spotify', 'Apple Music', 'YouTube Music', 'TikTok', 'Deezer'].map((platform) => (
              <span
                key={platform}
                className="text-white/40 hover:text-white text-lg font-display uppercase tracking-wider cursor-pointer transition-colors"
              >
                {platform}
              </span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ReleasesSection;
